// The D6 Merge destinations plus the owner-only Catalysts desk (owner selection
// 2026-08-27, docs/design/d6-cutover/PLAN.md): Calls is the intake main,
// Analysis is the research main, System sits pinned at the rail foot. Order
// and sentence-case labels follow the D6 prototype's catalog verbatim
// (design/five-directions-2026-08-26, d6/catalog.ts). The rail shows the
// groups; the context list shows every destination row under its group
// header. Ids are the router's ViewId set, so a missing or extra id is a
// compile error through the test.
import type { ViewId } from '../router/useView';
import type { WorkspaceRole } from '../api/types';

export type RailGroupId = 'calls' | 'desk' | 'analysis' | 'system';

export interface Destination {
  readonly id: ViewId;
  readonly label: string;
  readonly ownerOnly?: boolean;
}

export interface RailGroup {
  readonly id: RailGroupId;
  readonly label: string;
  // The destination the rail tile opens.
  readonly home: ViewId;
  readonly destinations: ReadonlyArray<Destination>;
  // System sits under the spacer in the rail.
  readonly pinned: boolean;
}

export const RAIL_GROUPS: ReadonlyArray<RailGroup> = [
  {
    id: 'calls',
    label: 'Calls',
    home: 'dashboard',
    pinned: false,
    destinations: [
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'live', label: 'Live calls' },
      { id: 'discover', label: 'Discover' },
      { id: 'watchlists', label: 'Watchlists' },
      { id: 'telegram', label: 'Telegram' },
      { id: 'discord', label: 'Discord' },
      { id: 'x', label: 'X' },
      { id: 'alerts', label: 'Alerts' },
    ],
  },
  {
    // Owner request 2026-08-27: the Research desk earns its own rail tile.
    id: 'desk',
    label: 'Desk',
    home: 'desk',
    pinned: false,
    destinations: [
      { id: 'desk', label: 'Research desk' },
      { id: 'catalysts', label: 'Catalysts', ownerOnly: true },
    ],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    home: 'checker',
    pinned: false,
    destinations: [
      { id: 'checker', label: 'Coin checker' },
      { id: 'relationships', label: 'Relationships' },
      { id: 'callers', label: 'Caller intelligence' },
      { id: 'creators', label: 'Creator history' },
      { id: 'markets', label: 'Markets' },
      { id: 'wallets', label: 'Wallets' },
      { id: 'coach', label: 'Coach' },
      { id: 'ledger', label: 'Evidence ledger' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    home: 'sources',
    pinned: true,
    destinations: [
      { id: 'sources', label: 'Sources' },
      { id: 'health', label: 'Provider health' },
      { id: 'settings', label: 'Settings' },
    ],
  },
];

export const DESTINATIONS: ReadonlyArray<Destination> = RAIL_GROUPS.flatMap(
  (group) => group.destinations,
);

export function destinationAllowed(destination: Destination, role: WorkspaceRole | null): boolean {
  return destination.ownerOnly !== true || role === 'owner';
}

export function destinationsForRole(role: WorkspaceRole | null): ReadonlyArray<Destination> {
  return DESTINATIONS.filter((destination) => destinationAllowed(destination, role));
}

export function railGroupsForRole(role: WorkspaceRole | null): ReadonlyArray<RailGroup> {
  return RAIL_GROUPS.map((group) => {
    const destinations = group.destinations.filter((destination) =>
      destinationAllowed(destination, role),
    );
    const home = destinations.some((destination) => destination.id === group.home)
      ? group.home
      : (destinations[0]?.id ?? group.home);
    return { ...group, home, destinations };
  }).filter((group) => group.destinations.length > 0);
}

export function canAccessView(view: ViewId, role: WorkspaceRole | null): boolean {
  const destination = DESTINATIONS.find((candidate) => candidate.id === view);
  return destination !== undefined && destinationAllowed(destination, role);
}

export function viewAccessReason(view: ViewId): string {
  return `${labelOf(view)} is limited to the owner role. The server checks the same boundary on every request.`;
}

export function groupOf(view: ViewId): RailGroup {
  const group = RAIL_GROUPS.find((candidate) =>
    candidate.destinations.some((destination) => destination.id === view),
  );
  if (!group) throw new Error(`View "${view}" is not in any rail group`);
  return group;
}

export function groupOfForRole(view: ViewId, role: WorkspaceRole | null): RailGroup {
  const group = groupOf(view);
  return railGroupsForRole(role).find((candidate) => candidate.id === group.id) ?? group;
}

export function labelOf(view: ViewId): string {
  const destination = DESTINATIONS.find((candidate) => candidate.id === view);
  return destination ? destination.label : view;
}
